import React, { useMemo, useState } from 'react';
import { syncUserProfileDebounced } from '../api/users';
import { clearAnalyticsCookies } from '../analytics';
import {
  getConsentPreferences,
  setConsentPreferences,
  snoozeConsentModal,
} from '../utils/consentPreferences';
import {
  safeLocalStorageGetItem,
  safeLocalStorageRemoveItem,
  safeLocalStorageSetItem,
} from '../utils/safeStorage';
import ConsentCategorySettings from './ConsentCategorySettings';
import FaIcon from './FaIcon';
import styles from '../styles/ConsentBanner.module.css';

const STRINGS = {
  it: {
    title: 'Giochini di Parole chiede il tuo consenso per utilizzare cookie e trattare i tuoi dati personali per le seguenti finalità:',
    subtitlePrefix: 'Usiamo cookie e tecnologie simili per far funzionare il servizio, capire come migliorarlo e, solo se acconsenti, inviarti comunicazioni promozionali. Puoi accettare tutto, rifiutare i non essenziali o personalizzare le categorie. Puoi aggiornare o revocare il consenso in qualsiasi momento da "Gestisci cookie" nella pagina qui sotto oppure nella pagina utente. Per i dettagli, leggi la',
    policyLinkLabel: 'Privacy Policy',
    subtitleSuffix: '.',
    acceptAll: 'Accetta tutto',
    rejectAll: 'Rifiuta non essenziali',
    customize: 'Personalizza',
    savePreferences: 'Salva preferenze',
    categoriesTitle: 'Categorie',
    necessary: 'Necessari',
    necessaryDescription: 'Sempre attivi per sicurezza, login e funzionamento base del servizio.',
    functional: 'Funzionali',
    functionalDescription: 'Memorizzano preferenze come lingua e impostazioni opzionali.',
    analytics: 'Analitici',
    analyticsDescription: 'Misurano utilizzo e includono anche session replay per migliorare il prodotto.',
    marketing: 'Marketing',
    marketingDescription: 'Usati per comunicazioni promozionali facoltative.',
    necessaryLockedMessage: 'I dati necessari non possono essere disattivati perché servono al funzionamento del servizio.',
    closeLabel: 'Chiudi popup consenso',
    languageSwitchPrefix: 'Read this in',
  },
  en: {
    title: 'Giochini di Parole asks for your consent to use cookies and process your personal data for the following purposes:',
    subtitlePrefix: 'We use cookies and similar technologies to run the service, understand how to improve it, and, only with your consent, send promotional updates. You can accept all, reject non-essential categories, or customize your choices. You can withdraw or update consent anytime from "Gestisci cookie" in the footer or in the user account page. For full details, read our',
    policyLinkLabel: 'Privacy Policy',
    subtitleSuffix: '.',
    acceptAll: 'Accept all',
    rejectAll: 'Reject non-essential',
    customize: 'Customize',
    savePreferences: 'Save preferences',
    categoriesTitle: 'Categories',
    necessary: 'Necessary',
    necessaryDescription: 'Always on for security, login, and core service functionality.',
    functional: 'Functional',
    functionalDescription: 'Stores preferences such as language and optional settings.',
    analytics: 'Analytics',
    analyticsDescription: 'Measures usage and includes session replay to improve the product.',
    marketing: 'Marketing',
    marketingDescription: 'Used for optional promotional communication.',
    necessaryLockedMessage: 'Necessary data cannot be turned off because it is required for core service functionality.',
    closeLabel: 'Close consent popup',
    languageSwitchPrefix: 'Leggi in',
  },
};

const resolveInitialLanguage = () => {
  try {
    const storedPolicyLang = safeLocalStorageGetItem('policy-lang');
    if (storedPolicyLang === 'it' || storedPolicyLang === 'en') {
      return storedPolicyLang;
    }
    const browserLang = (navigator.language || '').toLowerCase();
    return browserLang.startsWith('it') ? 'it' : 'en';
  } catch (_) {
    return 'it';
  }
};

const buildInitialCategories = () => {
  const preferences = getConsentPreferences();
  return {
    necessary: true,
    functional: preferences?.categories?.functional === true,
    analytics: preferences?.categories?.analytics === true,
    marketing: preferences?.categories?.marketing === true,
  };
};

const FUNCTIONAL_KEYS_TO_CLEAR = [
  'policy-lang',
  'language-level',
  'newsletter-signup-dismissed',
  'christmas-lottery-dismissed',
];

const ConsentBanner = ({ onConsentChange, onClose }) => {
  const [language, setLanguage] = useState(resolveInitialLanguage);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showNecessaryMessage, setShowNecessaryMessage] = useState(false);
  const [categories, setCategories] = useState(buildInitialCategories);

  const text = useMemo(() => STRINGS[language] || STRINGS.it, [language]);
  const nextLanguage = language === 'it' ? 'en' : 'it';
  const nextLanguageLabel = nextLanguage === 'en' ? 'English' : 'Italiano';

  const persistConsent = (nextCategories, source = 'modal') => {
    const current = getConsentPreferences();
    const preferences = setConsentPreferences({
      region: current?.region || 'eu',
      categories: nextCategories,
      decidedAt: new Date().toISOString(),
      source,
    });

    if (nextCategories.analytics !== true) {
      clearAnalyticsCookies();
    }

    if (nextCategories.functional !== true) {
      FUNCTIONAL_KEYS_TO_CLEAR.forEach((key) => safeLocalStorageRemoveItem(key));
    }

    syncUserProfileDebounced();
    onConsentChange(nextCategories.analytics === true);
    onClose();
    return preferences;
  };

  const handleAcceptAll = () => {
    persistConsent({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    persistConsent({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleSaveCustomized = () => {
    persistConsent(categories);
  };

  const handleDismiss = () => {
    snoozeConsentModal();
    const current = getConsentPreferences();
    onConsentChange(current?.categories?.analytics === true);
    onClose();
  };

  const handleCategoryToggle = (category, enabled) => {
    setCategories((prev) => ({
      ...prev,
      [category]: enabled,
    }));
  };

  const handleNecessaryAttempt = () => {
    setShowNecessaryMessage(true);
  };

  const handleLanguageToggle = () => {
    setLanguage(nextLanguage);
    safeLocalStorageSetItem('policy-lang', nextLanguage);
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleDismiss();
        }
      }}
    >
      <div className={styles.modal}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleDismiss}
          aria-label={text.closeLabel}
        >
          ×
        </button>

        <img className={styles.brandLogo} src={`${process.env.PUBLIC_URL || ''}/favicon-96x96.png`} alt="Giochini di Parole" width="32" height="32" />
        <h2 id="consent-modal-title" className={styles.title}>
          {text.title}
        </h2>

        {!showCustomize && (
          <ul className={styles.purposeList}>
            {[
              ['functional', 'fas fa-layer-group'],
              ['analytics', 'fas fa-chart-bar'],
              ['marketing', 'fas fa-bullhorn'],
            ].map(([category, icon]) => (
              <li key={category} className={styles.purposeRow}>
                <span className={styles.purposeIcon} aria-hidden="true"><FaIcon className={icon} /></span>
                <span>
                  <span className={styles.purposeLabel}>{text[category]}</span>
                  <span className={styles.purposeDescription}>{text[`${category}Description`]}</span>
                </span>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          className={styles.customizeToggle}
          aria-expanded={showCustomize}
          aria-controls="consent-category-options"
          onClick={() => {
            setShowCustomize((prev) => !prev);
            setShowNecessaryMessage(false);
          }}
        >
          <span className={styles.customizeIcon} aria-hidden="true">
            <FaIcon className={showCustomize ? 'fas fa-chevron-up' : 'fas fa-chevron-down'} />
          </span>
          {text.customize}
        </button>

        {showCustomize && (
          <div id="consent-category-options" className={styles.customizeSection}>
            <h3 className={styles.categoriesTitle}>{text.categoriesTitle}</h3>
            <ConsentCategorySettings
              variant="cmp"
              categories={categories}
              labels={{
                necessary: text.necessary,
                necessaryDescription: text.necessaryDescription,
                functional: text.functional,
                functionalDescription: text.functionalDescription,
                analytics: text.analytics,
                analyticsDescription: text.analyticsDescription,
                marketing: text.marketing,
                marketingDescription: text.marketingDescription,
              }}
              onCategoryToggle={handleCategoryToggle}
              onNecessaryAttempt={handleNecessaryAttempt}
              necessaryMessage={showNecessaryMessage ? text.necessaryLockedMessage : ''}
              necessaryMessageId="consent-modal-necessary-message"
            />

            <button type="button" className={styles.saveAction} onClick={handleSaveCustomized}>
              {text.savePreferences}
            </button>
          </div>
        )}

        <p className={styles.subtitle}>
          {text.subtitlePrefix}{' '}
          <a className={styles.policyLink} href="/privacy">
            {text.policyLinkLabel}
          </a>
          {text.subtitleSuffix}
        </p>
        <p className={styles.languageToggleRow}>
          {text.languageSwitchPrefix}{' '}
          <button
            type="button"
            className={styles.languageToggleLink}
            onClick={handleLanguageToggle}
          >
            {nextLanguageLabel}
          </button>
        </p>

        <div className={styles.actions}>
          <button type="button" className={styles.secondaryAction} onClick={handleRejectAll}>
            {text.rejectAll}
          </button>
          <button type="button" className={styles.primaryAction} onClick={handleAcceptAll}>
            {text.acceptAll}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
